import React, { useRef, useState, useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from '@mui/material/styles';
import { StyledModal } from 'src/presentations/components/molecules/modal';
import Button from 'src/presentations/components/atom/button';
import { useBilledEntities } from 'src/use-cases/settings/useBilledEntities';
import moneyTimeIcon from 'src/assets/images/icons/money-time.svg';
import { BilledEntitiesMenu } from './BilledEntitiesMenu';
import { CreditSummaryCard } from './CreditSummaryCards';
import { baseInputStyles, EntitySelectButton, findEntityById, CalendarIcon } from './creditTransactionsUtils';

// Validation schema
const validationSchema = Yup.object({
  billedEntityId: Yup.string().required('Credit entity is required'),
  startDate: Yup.date().nullable().required('Start date is required').typeError('Invalid date'),
  endDate: Yup.date().nullable().required('End date is required').typeError('Invalid date'),
}).required();

type StartBillingCycleFormValues = Yup.InferType<typeof validationSchema>;

interface StartBillingCycleModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: StartBillingCycleFormValues) => void;
  isLoading?: boolean;
}

const CustomStyledModal = styled(StyledModal)<{ width?: number }>(({ width }) => {
  const modalWidth = width ?? 500;
  return {
    '& .MuiDialog-paper': {
      maxWidth: `${modalWidth}px`,
      borderRadius: '16px',
      overflow: 'visible',
    },
    '& .MuiDialogContent-root': {
      overflow: 'visible',
    },
  };
});

const StyledDatePicker = styled(DatePicker)(() => ({
  ...baseInputStyles,
  '& .MuiInputAdornment-root': {
    marginRight: '8px',
  },
}));

export const StartBillingCycleModal: React.FC<StartBillingCycleModalProps> = ({ open, onClose, onSubmit, isLoading = false }) => {
  const [isBilledEntitiesMenuVisible, setIsBilledEntitiesMenuVisible] = useState(false);
  const billedEntitiesButtonRef = useRef<HTMLButtonElement>(null);

  const { data: billedEntities } = useBilledEntities();

  const formContext = useForm<StartBillingCycleFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      billedEntityId: '',
      startDate: null,
      endDate: null,
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
    trigger,
  } = formContext;

  const selectedBilledEntityId = watch('billedEntityId');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      reset({
        billedEntityId: '',
        startDate: null,
        endDate: null,
      });
    }
  }, [open, reset]);

  const selectedEntity = selectedBilledEntityId && billedEntities ? findEntityById(billedEntities, Number(selectedBilledEntityId)) : null;
  const entityDisplayText = selectedEntity?.code || 'Select Credit Entity';

  const handleBilledEntitiesToggle = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setIsBilledEntitiesMenuVisible(!isBilledEntitiesMenuVisible);
  };

  const handleBilledEntitiesClose = () => {
    setIsBilledEntitiesMenuVisible(false);
  };

  const handleBilledEntitiesApply = (selectedIds: string[]) => {
    if (selectedIds.length > 0) {
      setValue('billedEntityId', selectedIds[0]);
      trigger('billedEntityId');
    }
    setIsBilledEntitiesMenuVisible(false);
  };

  const onFormSubmit = (values: StartBillingCycleFormValues) => {
    onSubmit(values);
  };

  return (
    <FormProvider {...formContext}>
      <CustomStyledModal open={open} title='Start Billing Cycle' fullWidth onClose={!isLoading && onClose} width={500}>
        <div className='relative'>
          {/* Entity Selection */}
          <div className='mt-8'>
            <p className='text-14 font-normal text-blue_grey-80 mb-20'>Which entity are you billing?</p>
            <div className='relative'>
              <div className='relative'>
                {selectedBilledEntityId && (
                  <span className='absolute -top-8 left-10 bg-white px-4 text-[0.625rem] text-grey-10 z-10'>Select Credit Entity</span>
                )}
                <EntitySelectButton
                  id='entity-select'
                  ref={billedEntitiesButtonRef}
                  onClick={handleBilledEntitiesToggle}
                  type='button'
                  hasError={!!errors.billedEntityId}
                >
                  <span
                    className={!selectedBilledEntityId ? 'text-neutral-50 text-14 xl:text-16' : 'text-grey-10 text-14 xl:text-16'}
                    style={{ color: errors.billedEntityId ? '#d32f2f' : undefined }}
                  >
                    {entityDisplayText}
                  </span>
                  <svg
                    className='w-20 h-20 text-neutral-50'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                    style={{
                      transform: isBilledEntitiesMenuVisible ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease-in-out',
                    }}
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                  </svg>
                </EntitySelectButton>
              </div>
              {errors.billedEntityId && (
                <p className='text-12 mt-4' style={{ color: '#d32f2f' }}>
                  {errors.billedEntityId.message}
                </p>
              )}
              <BilledEntitiesMenu
                billedEntities={billedEntities || []}
                isVisible={isBilledEntitiesMenuVisible}
                onClose={handleBilledEntitiesClose}
                onToggle={handleBilledEntitiesToggle}
                onApply={handleBilledEntitiesApply}
                toggleButtonRef={billedEntitiesButtonRef}
                configOverrides={{
                  maxHeight: 'calc(100vh - 350px)',
                  height: 'auto',
                }}
              />
            </div>
          </div>

          {/* Date Pickers Row */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className='mt-24 grid grid-cols-2 gap-16'>
              <Controller
                name='startDate'
                control={control}
                render={({ field }) => (
                  <StyledDatePicker
                    {...field}
                    label='Start Date'
                    format='dd-MMM-yyyy'
                    slots={{
                      openPickerIcon: CalendarIcon,
                    }}
                    slotProps={{
                      textField: {
                        placeholder: '10-Dec-2025',
                        error: !!errors.startDate,
                        helperText: errors.startDate?.message,
                      },
                      popper: {
                        disablePortal: true,
                      },
                    }}
                  />
                )}
              />
              <Controller
                name='endDate'
                control={control}
                render={({ field }) => (
                  <StyledDatePicker
                    {...field}
                    label='End Date'
                    format='dd-MMM-yyyy'
                    slots={{
                      openPickerIcon: CalendarIcon,
                    }}
                    slotProps={{
                      textField: {
                        placeholder: '10-Dec-2025',
                        error: !!errors.endDate,
                        helperText: errors.endDate?.message,
                      },
                      popper: {
                        disablePortal: true,
                      },
                    }}
                  />
                )}
              />
            </div>
          </LocalizationProvider>

          {/* Outstanding R&A Card */}
          <div className='mt-24'>
            <CreditSummaryCard title='Outstanding' icon={moneyTimeIcon} invoiceValue={404} shipmentValue={100} gradientColor='rgba(231,37,71,0.70)' />
          </div>

          {/* Action Buttons */}
          <div className='mt-48 grid gap-16 grid-cols-2 grid-rows-1'>
            <Button variant='green_outline' onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleSubmit(onFormSubmit)} isLoading={isLoading}>
              Confirm
            </Button>
          </div>
        </div>
      </CustomStyledModal>
    </FormProvider>
  );
};
